import type { Manifest, Predicate } from "@procore/bim-index";

/**
 * Wire protocol between the BIM-index worker and its callers.
 *
 * The engine reads the index through OPFS sync access handles, which exist only
 * in a worker — so that is where it lives, and every query runs off the main
 * thread. Whatever the engine does hold (decoded pages, a result bitmap) comes
 * back in one go when the worker is dropped.
 */

/** The eight files a full index is made of; only `manifest.json` is required. */
export const INDEX_FILES = [
    "manifest.json",
    "id_mappings.idx",
    "properties.idx",
    "object_tree.idx",
    "property_tree.idx",
    "search.idx",
    // Object geometry, as of manifest format 3.0 — before that, bounds were
    // synthetic `$bounds/*` columns inside `properties.idx`. Spatial predicates
    // and the enclosing box behind "zoom to selection" are answered from this,
    // and an index built by an older producer simply has none.
    "bounds.idx",
    // Which ids denote a real object rather than an upstream placeholder slot.
    // Any predicate that complements — `NOT`, match-all — is answered from this,
    // and fails without it.
    "live_objects.rbm",
] as const;

export type IndexFile = (typeof INDEX_FILES)[number];

export type OpenResult = {
    manifest: Manifest;
    objectCount: number;
    propertyCount: number;
    /** Which files were actually present and handed to the engine. */
    loaded: IndexFile[];
    /** Milliseconds spent opening the files, and constructing the engine. */
    openMs: number;
    constructMs: number;
    /**
     * WASM linear memory in bytes right after construction. Sections are read on
     * demand, so this is the engine's own footprint rather than the index's size
     * — it grows as queries decode pages, up to the caches' own budgets.
     */
    wasmMemoryBytes: number;
    bimIndexVersion: string;
};

export type PropertyCapability = {
    name: string;
    index_type: string;
    cardinality: number;
    unit?: string;
    dimension?: string;
};

export type NodeRow = {
    id: number;
    name: string;
    /** Full `/`-separated path, resolved in the worker so a page is one call. */
    path: string;
    descendantCount: number;
    hasChildren: boolean;
};

export type ResultRow = {
    /** Object id — identical to the viewer's `ObjectId` for a Procore scene. */
    id: number;
    name: string;
    path: string;
    /** Procore instance id, or null when the object has none. */
    externalId: string | null;
};

export type Bounds = { min: [number, number, number]; max: [number, number, number] };

export type Sphere = { center: [number, number, number]; radius: number };

/**
 * The volume enclosing a set of objects. Both halves are optional: only the part(s) the
 * request asked for are computed, and each is folded independently over the selection,
 * so neither necessarily encloses the other.
 */
export type EnclosingBounds = { box?: Bounds; sphere?: Sphere };

/**
 * A query result set. The full match set stays in the worker as a roaring
 * bitmap (`query` keeps it as the "current result"); only a page of rows and,
 * on request, a capped id array cross to the main thread.
 */
export type QueryResult = {
    total: number;
    degraded: boolean;
    hints: unknown[];
    rows: ResultRow[];
    bounds: Bounds | null;
    tookMs: number;
};

export type AggregateEntry = {
    op: string;
    property: string;
    value: number | null;
    unit?: string;
    dimension?: string;
    count_defined: number;
    count_indexed: number;
};

export type AggregateGroup = {
    /** `null` is the matched-but-undefined bucket. */
    key: string | null;
    count_matched: number;
    aggregates: AggregateEntry[];
};

export type AggregateResponse = {
    total_matches: number;
    degraded: boolean;
    hints?: unknown[];
    /** Flat results — present for an ungrouped request. */
    results?: AggregateEntry[];
    /** Per-group results — present only when `group_by` was requested. */
    groups?: AggregateGroup[];
};

export type AggregateResult = {
    tookMs: number;
    response: AggregateResponse;
};

export type Requests = {
    /**
     * Open the index files in OPFS and construct the engine over them. The worker
     * never downloads: `dir` is the full `/`-separated path of the folder the
     * files sit directly in — e.g. `/<sceneId>/bim_index` — which someone else has
     * already populated, and a section missing from it is simply not loaded.
     * Nothing is read up front; the engine pulls byte ranges as queries need
     * them. `only` restricts which sections are opened: a client pays for just
     * the capabilities it uses (skipping `search.idx` drops full-text search,
     * skipping `id_mappings.idx` drops Procore id resolution, and so on).
     *
     * `cacheBudgetBytes` caps the engine's decompressed property-index cache,
     * which is the largest thing it retains now that sections are read on demand
     * — but not the only one, since the search posting cache and the tree section
     * caches carry their own limits. Omit it to accept the library's default,
     * which is sized for a native process rather than for a tab sharing its
     * address space with a renderer.
     */
    open: {
        params: { dir: string; only?: IndexFile[]; cacheBudgetBytes?: number };
        result: OpenResult;
    };
    /**
     * Drop the index and release the OPFS files. Worth calling before terminating
     * the worker, which reclaims the memory but cannot run code to unlock the
     * files.
     */
    close: { params: void; result: void };
    /** Run a predicate; keeps the match set as the worker's current result. */
    query: { params: { predicate: Predicate; offset: number; limit: number }; result: QueryResult };
    /** Page the current result set without re-running the query. */
    resultPage: { params: { offset: number; limit: number }; result: ResultRow[] };
    /** Ids of the current result set, capped — what the viewer highlights. */
    resultIds: { params: { max: number }; result: { ids: Uint32Array; capped: boolean } };
    /** Restrict the current result set to a subtree, returning the new totals. */
    scopeToSubtree: { params: { id: number; offset: number; limit: number }; result: QueryResult };
    /** Make a tree node's whole subtree the current result set. */
    subtreeResult: { params: { id: number; offset: number; limit: number }; result: QueryResult };
    count: { params: { predicate: Predicate }; result: number };
    listProperties: {
        params: { limit: number; cursor: string | null };
        result: { items: PropertyCapability[]; total: number; next_cursor: string | null };
    };
    propertyTree: {
        params: { path: string; limit: number; cursor: string | null };
        result: {
            items: { segment: string; full_path: string; has_children: boolean; has_values: boolean }[];
            next_cursor: string | null;
        };
    };
    propertyValues: {
        params: { path: string; limit: number; cursor: string | null };
        result: { items: { value: string; count: number }[]; next_cursor: string | null };
    };
    modelTree: {
        params: { id: number | null; limit: number; cursor: string | null };
        result: { items: NodeRow[]; next_cursor: string | null };
    };
    /** Resolve a Procore instance id back to an object id. */
    objectIdForExternal: { params: { externalId: string }; result: number | null };
    /** Immediate parent of an object, or `null` for a root. */
    parentId: { params: { id: number }; result: number | null };
    /**
     * Object id of the first node at `path`. `$path` is a hash-indexed synthetic
     * property, so this is an equality lookup rather than a scan.
     */
    objectIdAtPath: { params: { path: string }; result: number | null };
    /** The ancestor chain of an object, root first, excluding the object itself. */
    ancestors: { params: { id: number }; result: { id: number; path: string }[] };
    /** Ids of an object's whole subtree. */
    descendantIds: { params: { id: number }; result: Uint32Array };
    /**
     * One page of a predicate's matches as tree rows. Stateless — unlike `query` it
     * does not become the current result set, so concurrent callers cannot clobber
     * each other.
     */
    matchRows: {
        params: { predicate: Predicate; offset: number; limit: number };
        result: { items: { id: number; path: string; hasChildren: boolean }[]; total: number };
    };
    /**
     * Ids matching `predicate`, optionally unioned with every match's subtree.
     *
     * The match bitmap goes straight into `expand` inside the worker, so no ids cross
     * the boundary between the two steps and none are materialized in between. Stateless
     * like `matchRows`: it never touches the current result set.
     *
     * `degraded` reports a scan deadline hit on either step — the ids are correct, but
     * the set covers less than was asked for with nothing to name what is missing.
     */
    matchIds: {
        params: { predicate: Predicate; includeDescendants: boolean };
        result: { ids: Uint32Array; degraded: boolean };
    };
    /**
     * What a click on `id` selects: the object it stands for and that object's whole
     * subtree — `climb` and `expand` fused, so nothing crosses the boundary between the two
     * steps (`matchIds`' reason).
     *
     * @param params.policy Which matching ancestor answers — `nearest` the deepest,
     *   `topmost` the shallowest.
     * @returns `id: null` when the chain held no match, a caller's cue to fall back rather
     *   than an error; `ids` is then empty. Stateless like {@link matchIds}.
     */
    resolveSelection: {
        params: { id: number; target: Predicate; policy: "nearest" | "topmost"; includeSelf: boolean };
        result: { id: number | null; ids: Uint32Array; degraded: boolean };
    };
    /**
     * Ids of the union of serialized roaring bitmaps, with an optional id array
     * unioned in — how a **served** index's result set becomes ids.
     *
     * The BIM Query API answers its bitmap-backed reads with the whole set as
     * portable roaring bytes (`Accept: application/vnd.procore.bim.roaring`) instead
     * of a thousand JSON pages, and this is where those bytes are decoded: the same
     * WASM that reads a local index deserializes them, off the main thread, and the
     * ids come back transferred rather than copied.
     *
     * Needs **no open index** — only the module, which is what deserializes. That is
     * the point: the scenes this serves are the ones with no local index to open.
     *
     * Several bitmaps rather than one because a served result routinely takes more
     * than one request — a predicate too wide for the engine to plan, a batch of
     * expansion roots, a scan resumed through its `Bim-Resume-Token`. Unioning them
     * here is one native op per set; unioning them on the main thread would mean a
     * `Set<number>` over hundreds of thousands of ids. `unionWith` carries the one
     * set that is already ids rather than bytes: the match ids that had to be
     * materialized to be sent back up as expansion roots.
     */
    bitmapIds: {
        params: { bitmaps: Uint8Array[]; unionWith?: Uint32Array };
        result: Uint32Array;
    };
    /**
     * The volume enclosing a predicate's matches — the engine's own "zoom to selection"
     * primitive, folded off the bounds table without materializing a single row.
     *
     * Stateless like {@link matchIds} and for the same reason: `query`'s current result
     * is shared with `consoleApi`, so two overlapping callers would read each other's
     * match set.
     *
     * `null` when nothing in the match set has geometry — or when `bounds.idx` was not
     * loaded, since that is where the geometry lives. Asking for one part rather than
     * both roughly halves the work, as the two probes cost about the same.
     */
    bounds: {
        params: { predicate: Predicate; parts: "box" | "sphere" | "both" };
        result: EnclosingBounds | null;
    };
    /**
     * COUNT/SUM/AVG/MIN/MAX over a predicate's matches, optionally grouped.
     *
     * The engine is strict about what it will aggregate, and says so by failing
     * rather than by answering empty: `property` is required even for `COUNT`, and
     * has to be a numeric property; `groupBy.property` has to be an inverted one,
     * so grouping by a `hash` property is an error. `groupBy.limit` is a hard cap
     * rather than a top-N — more non-empty groups than that fails the request
     * instead of truncating it.
     */
    aggregate: {
        params: {
            predicate: Predicate;
            aggregations: { op: string; property: string; unit?: string; p?: number }[];
            groupBy?: { property: string; limit: number; include_undefined: boolean };
        };
        result: AggregateResult;
    };
};

export type Method = keyof Requests;

export type WorkerRequest = {
    [M in Method]: { kind: "request"; id: number; method: M; params: Requests[M]["params"] };
}[Method];

export type WorkerResponse =
    | { kind: "response"; id: number; ok: true; result: unknown }
    | { kind: "response"; id: number; ok: false; error: string };
